import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getAuthToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Calendar, XCircle, ExternalLink, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Subscription Management card — visible only inside the Owner-unlocked
 * Private Portal. Reuses /api/billing/status + /api/billing/cancel. For the
 * MASTER_JOHN tenant this card simply shows the "lifetime · master" badge.
 */
export default function SubscriptionManagement() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activating, setActivating] = useState(false);
  const [paypalRef, setPaypalRef] = useState("");
  const nav = useNavigate();

  const load = () => {
    if (!getAuthToken()) {
      // Legacy MASTER_JOHN device-locked kiosks have no JWT. Show a static
      // lifetime tile so the card still renders cleanly.
      setS({ tenant_id: "MASTER_JOHN", subscription_status: "active", subscription_plan: "Lifetime · MASTER", legacy: true });
      return;
    }
    api.get("/billing/status").then(({ data }) => setS(data)).catch(() => setS(null));
  };

  useEffect(() => { load(); }, []);

  const isMaster = s?.tenant_id === "MASTER_JOHN";

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You will retain access until the end of the current billing period. You must ALSO cancel the recurring profile inside your PayPal account.")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel", { reason: "Owner clicked Cancel from Portal" });
      toast.success("Subscription marked canceled locally — remember to stop autopay inside PayPal too");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  const manualActivate = async () => {
    if (!window.confirm("Confirm you have completed the PayPal subscription payment. We will activate your account now and PayPal will email you the receipt.")) return;
    setActivating(true);
    try {
      await api.post("/billing/manual-activate", { paypal_reference: paypalRef.trim() || null });
      toast.success("Subscription activated · Portal unlocked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Activation failed");
    } finally {
      setActivating(false);
    }
  };

  if (!s) return null;

  const active = s.subscription_status === "active";
  const canceled = s.subscription_status === "canceled";
  const trial = s.subscription_status === "trial";

  return (
    <Card className="p-5" data-testid="subscription-management">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center">
          {isMaster ? <Crown className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />}
        </div>
        <div>
          <h3 className="font-bold text-lg leading-tight">Subscription Management</h3>
          <p className="text-[11px] text-slate-500 tracking-wide">
            Διαχείριση Συνδρομής · Owner only
          </p>
        </div>
      </div>

      <div className="space-y-2 mb-4 text-sm">
        <Row label="Plan · Πρόγραμμα" value={s.subscription_plan} />
        <Row label="Status · Κατάσταση" value={
          <Badge className={
            active ? "bg-emerald-100 text-emerald-800"
            : trial ? "bg-amber-100 text-amber-800"
            : canceled ? "bg-slate-200 text-slate-700"
            : "bg-red-100 text-red-800"
          }>{(s.subscription_status || "").toUpperCase()}</Badge>
        } />
        {s.trial_days_left != null && (
          <Row label="Trial days left" value={<span className="font-mono">{s.trial_days_left}</span>} />
        )}
        {s.paypal_subscription_id && (
          <Row label="PayPal Subscription ID" value={<code className="text-xs font-mono">{s.paypal_subscription_id}</code>} />
        )}
        {s.monthly_price_eur != null && !isMaster && (
          <Row label="Price · Τιμή" value={<span className="font-mono">€{s.monthly_price_eur.toFixed(2)} / month</span>} />
        )}
      </div>

      {isMaster ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-900 text-xs flex items-center gap-2">
          <Crown className="h-4 w-4 shrink-0" />
          <span>MASTER_JOHN tenant · lifetime free. No subscription required.</span>
        </div>
      ) : active ? (
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => nav("/billing")}
            variant="outline"
            className="gap-2 h-10"
            data-testid="sub-mgmt-open-billing"
          >
            <ExternalLink className="h-4 w-4" />
            <span className="text-xs">Open Billing Page</span>
          </Button>
          <Button
            onClick={cancel}
            disabled={busy}
            variant="outline"
            className="gap-2 h-10 border-red-300 text-red-700 hover:bg-red-50"
            data-testid="sub-mgmt-cancel"
          >
            <XCircle className="h-4 w-4" />
            <span className="text-xs font-bold">
              {busy ? "Cancelling…" : "Cancel Subscription · Ακύρωση"}
            </span>
          </Button>
        </div>
      ) : canceled ? (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-slate-700 text-xs">
          <Calendar className="inline h-3 w-3 mr-1" />
          Subscription canceled. Access continues until the end of the current paid period.
          {" "}
          <button onClick={() => nav("/billing")} className="underline font-bold" data-testid="sub-mgmt-resubscribe">
            Resubscribe
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="paypal-hosted-form-wrap">
          {/* === Live hosted PayPal subscription form ====================
              Hosted Button ID 82PVJ439WEH9A · Merchant Giannisleo51@gmail.com
              The form submits to PayPal directly (no API integration); the
              tenant returns to our app via PayPal's account page.
          =============================================================== */}
          <form
            action="https://www.paypal.com/cgi-bin/webscr"
            method="post"
            target="_top"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              margin: "20px 0",
            }}
            data-testid="paypal-hosted-form"
          >
            <input type="hidden" name="cmd" value="_s-xclick" />
            <input type="hidden" name="hosted_button_id" value="82PVJ439WEH9A" />
            <table style={{ marginBottom: "15px", width: "100%", textAlign: "center" }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: "bold", color: "#333", paddingBottom: "5px" }}>
                    <input type="hidden" name="on0" value="Καλωσήρθες στο KioskForce Erp" />
                    Καλωσήρθες στο KioskForce Erp
                  </td>
                </tr>
                <tr>
                  <td>
                    <select
                      name="os0"
                      style={{
                        padding: "8px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        width: "90%",
                        maxWidth: "400px",
                        backgroundColor: "#f9f9f9",
                        fontSize: "14px",
                      }}
                      defaultValue="Κουράστηκες να μετράς μετρητά στο End Shift και να ψάχνεις!!"
                    >
                      <option value="Κουράστηκες να μετράς μετρητά στο End Shift και να ψάχνεις!!">
                        Κουράστηκες να μετράς μετρητά στο End Shift και να ψάχνεις!!
                      </option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
            <input type="hidden" name="currency_code" value="EUR" />
            <input
              type="image"
              src="https://www.paypalobjects.com/en_US/i/btn/btn_subscribe_SM.gif"
              border="0"
              name="submit"
              title="PayPal - The safer, easier way to pay online!"
              alt="Subscribe"
              style={{ cursor: "pointer", transform: "scale(1.2)", marginTop: "10px" }}
              data-testid="paypal-hosted-submit"
            />
          </form>

          {/* Manual activation hook — flips subscription_status to 'active'
              once the tenant returns after completing the PayPal payment. */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="manual-activate-block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
              Step 2 · Already paid? Activate now
            </div>
            <p className="text-xs text-slate-600 mb-2">
              Once PayPal confirms your €8/month subscription, paste the
              transaction reference (optional) and click <span className="font-bold">Activate</span> to
              unlock your Portal immediately.
            </p>
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={paypalRef}
                onChange={(e) => setPaypalRef(e.target.value)}
                placeholder="PayPal transaction ID (optional)"
                className="flex-1 min-w-[200px] h-10 px-3 rounded-md border border-slate-300 font-mono text-xs"
                data-testid="manual-activate-ref"
              />
              <Button
                onClick={manualActivate}
                disabled={activating}
                className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2"
                data-testid="manual-activate-btn"
              >
                <CreditCard className="h-4 w-4" />
                {activating ? "Activating…" : "I have paid · Activate"}
              </Button>
            </div>
            <p className="text-[10px] text-amber-700 mt-2">
              ⚠ Activation trusts the tenant. PayPal webhook overrides this when configured.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</span>
      <span className="text-slate-900 font-semibold">{value || "—"}</span>
    </div>
  );
}
